import { ref, watch } from 'vue';
import { useLens } from '@/composables/api/lens/useLens';
import router from '@/router';
import { pageTitle } from '@/components/lens/lensPanelPages';

const CAPABILITY_ID = 'lens.chat';

// Module-level: the header icon and the panel share one conversation, and it outlives navigation.
const open = ref(false);
const messages = ref([]);
const isStreaming = ref(false);

let lens = null;
// The assistant message the current turn streams into.
let current = null;
// Bumped by every send and by stop/startOver, so a superseded turn's late finish changes nothing.
let turn = 0;

function errorOf(event) {
    return { message: event?.error?.message ?? 'Something went wrong.', code: event?.error?.code ?? 'error' };
}

function onEvent(event) {
    if (!current) return;
    switch (event?.type) {
        case 'text':
            current.content = lens.text.value || current.content + (event.delta ?? '');
            break;
        case 'result':
            // Server-built links to the help articles the reply used (#6453).
            if (event.result?.citations?.length) current.citations = event.result.citations;
            break;
        case 'error':
            current.error = errorOf(event);
            isStreaming.value = false;
            break;
        case 'done':
            isStreaming.value = false;
            break;
        default:
            break;
    }
}

function ensureLens() {
    if (lens) return lens;
    lens = useLens(CAPABILITY_ID, { onEvent });
    watch(lens.text, (text) => {
        if (current && text) current.content = text;
    });
    // A refusal before streaming started (PHI screen, rate limit, network) sets error without an event.
    watch(lens.error, (error) => {
        if (current && error) {
            current.error = { message: error.message, code: error.code };
            isStreaming.value = false;
        }
    });
    return lens;
}

/** Where the user is: the route name and the page's title, never params, query or URL. */
function currentPage() {
    const route = router.currentRoute?.value ?? {};
    return { routeName: String(route.name ?? ''), label: pageTitle(route) };
}

async function send(text) {
    const message = String(text ?? '').trim();
    if (!message || isStreaming.value) return;

    ensureLens();
    const thisTurn = ++turn;
    messages.value.push({ role: 'user', content: message });
    messages.value.push({ role: 'assistant', content: '', error: null });
    // The reactive proxy, so streamed updates re-render the transcript.
    current = messages.value[messages.value.length - 1];
    isStreaming.value = true;

    try {
        await lens.send(message, { page: currentPage() });
    } catch (error) {
        console.error('Lens panel request failed', { capabilityId: CAPABILITY_ID, conversationId: lens.conversationId.value, error });
        if (thisTurn === turn && current) current.error = { message: 'The AI service could not be reached. Please try again.', code: 'network_error' };
    }

    if (thisTurn !== turn) return;
    if (current) {
        if (lens.text.value) current.content = lens.text.value;
        if (lens.error.value && !current.error) current.error = { message: lens.error.value.message, code: lens.error.value.code };
    }
    isStreaming.value = false;
}

/** Aborts the reply; what already streamed stays in the transcript. */
function stop() {
    turn++;
    lens?.stop();
    isStreaming.value = false;
}

/** A fresh conversation: stops any reply and forgets the server-side conversation. */
function startOver() {
    stop();
    current = null;
    messages.value = [];
    if (lens) lens.conversationId.value = null;
}

function toggle() {
    open.value = !open.value;
}

function close() {
    open.value = false;
}

/**
 * The header Lens panel's state and chat over the `lens.chat` capability (#6435). Every call
 * returns the same state.
 */
export function useLensPanel() {
    return { open, messages, isStreaming, send, stop, startOver, toggle, close };
}
